import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const ProgramAvailabilities = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const [availabilities, setAvailabilities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch availabilities data (wrapped in useCallback)
  const fetchAvailabilities = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getAvailabilities(programId); // Use programId prop
      setAvailabilities(data || []); // Ensure data is an array
    } catch (err) {
      console.error('Failed to fetch program availabilities:', err);
      setError(t('programAvailabilities.error'));
      setAvailabilities([]); // Set to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [programId]); // Dependency on programId

  // Fetch data on mount or when programId changes
  useEffect(() => {
    fetchAvailabilities();
  }, [fetchAvailabilities]);

  // Format date to a more readable format
  // Note: Date-only strings like "2012-06-20" are parsed as UTC midnight,
  // which can shift to the previous day when converted to local time.
  // We use UTC methods to avoid this timezone issue.
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">{t('programAvailabilities.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-4">{t('programAvailabilities.error')}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={fetchAvailabilities} // Retry fetch
          >
            {t('programTitles.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!availabilities || availabilities.length === 0) { // Check if availabilities is null or empty
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('programAvailabilities.noAvailabilities')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-auto">
      <div className="min-w-full">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left sticky top-0">
            <tr>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.availabilityId')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.catalogName')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.license')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.availableFrom')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.expiresAt')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.price')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.currency')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.videoQuality')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAvailabilities.columns.updateDate')}</th>
            </tr>
          </thead>
          <tbody>
            {availabilities.map((availability, index) => (
              <tr key={availability.availabilityId || index} className="border-t border-gray-200 hover:bg-gray-50">
                <td className="px-4 py-3">{availability.availabilityId || '-'}</td>
                <td className="px-4 py-3">{availability.catalogName || '-'}</td>
                <td className="px-4 py-3">{availability.license || '-'}</td>
                <td className="px-4 py-3">{formatDate(availability.availableFrom)}</td>
                <td className="px-4 py-3">{formatDate(availability.expiresAt)}</td>
                <td className="px-4 py-3">{availability.price || '-'}</td>
                <td className="px-4 py-3">{availability.currency || '-'}</td>
                <td className="px-4 py-3">{availability.videoQuality || availability.viewing_videoQuality || '-'}</td>
                <td className="px-4 py-3">{formatDate(availability.updateDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-between text-xs text-gray-500 px-4 pb-4">
          <div>
            {t('programAvailabilities.showing', { count: availabilities.length })}
          </div>
          <div>
            <button
              className={`px-3 py-1 border border-gray-300 rounded-sm ${availabilities.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              onClick={() => exportToCsv(availabilities, `${programId}_availabilities.csv`)}
              disabled={availabilities.length === 0}
            >
              {t('programAvailabilities.export')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramAvailabilities;
